import { useRef, useState } from 'react';
import { TYPE_ICONS, formatDate } from '../constants';

const TYPE_TAG_CLASS = {
  image: 'tag-image', video: 'tag-video', text: 'tag-text',
  longform: 'tag-longform', code: 'tag-code', prompt: 'tag-prompt',
  distilled: 'tag-prompt', sop: 'tag-code',
};
const TYPE_LABELS = {
  image: 'Image', video: 'Video', text: 'Text',
  longform: 'Long Form', code: 'Code', prompt: 'Prompt',
  distilled: 'Summary', sop: 'SOP',
};

const PROMPT_PREVIEW_LEN = 200;

// Platform detection from model string
const PLATFORMS = [
  { match: /chatgpt|gpt-?4|gpt-?3|gpt|o1|o3/i,      name: 'ChatGPT',    color: '#10A37F', logo: '/logos/chatgpt.png'    },
  { match: /claude/i,                                  name: 'Claude',     color: '#D97706', logo: '/logos/claude.png'     },
  { match: /gemini|bard/i,                             name: 'Gemini',     color: '#4285F4', logo: '/logos/gemini.png'     },
  { match: /perplexity/i,                              name: 'Perplexity', color: '#20B2AA', logo: '/logos/perplexity.png' },
  { match: /grok/i,                                    name: 'Grok',       color: '#1DA1F2', logo: '/logos/grok.png'       },
  { match: /copilot/i,                                 name: 'Copilot',    color: '#0078D4', logo: '/logos/copilot.png'    },
  { match: /higgsfield/i,                              name: 'Higgsfield', color: '#F5A623', logo: '/logos/higgsfield.png' },
  { match: /midjourney|mj/i,                           name: 'Midjourney', color: '#6B46C1', logo: '/logos/midjourney.png' },
  { match: /runway/i,                                  name: 'Runway',     color: '#e24b4a', logo: '/logos/runway.png'     },
  { match: /dall-?e|dalle/i,                           name: 'DALL·E',     color: '#10A37F', logo: '/logos/chatgpt.png'    },
];

function detectPlatform(model) {
  if (!model) return null;
  return PLATFORMS.find(p => p.match.test(model)) || null;
}

const LONG_PRESS_MS = 500;

export default function EntryCard({
  entry, selected, onClick,
  selectMode, isChecked, onCheck,
  onDragStart,
}) {
  const pressTimer   = useRef(null);
  const didLongPress = useRef(false);
  const isDragging   = useRef(false);
  const [hovered, setHovered] = useState(false);

  // ── Mobile: long press to select, tap to open ───────────────
  // Desktop: hover checkbox to select, click to open
  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse') return; // desktop handled by click
    setHovered(false); // prevent checkbox flash on touch
    didLongPress.current = false;
    pressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      // Haptic feedback if available
      if (navigator.vibrate) navigator.vibrate(40);
      onCheck && onCheck(entry.id);
    }, LONG_PRESS_MS);
  };

  const onPointerUp    = () => clearTimeout(pressTimer.current);
  const onPointerLeave = () => { clearTimeout(pressTimer.current); setHovered(false); };

  const handleClick = (e) => {
    if (didLongPress.current) { didLongPress.current = false; return; }
    if (isDragging.current) return;
    // Desktop in select mode: tap checks/unchecks
    // Mobile: tap ALWAYS opens — long press is used to select
    const isMouse = e?.nativeEvent?.pointerType === 'mouse';
    if (selectMode && isMouse) { onCheck && onCheck(entry.id); return; }
    onClick();
  };

  // Prompt preview — truncate to ~200 chars at word boundary
  // For SOPs strip markdown symbols for the card preview
  const rawPrompt     = entry.prompt || '';
  const promptFull    = entry.type === 'sop'
    ? rawPrompt.replace(/#{1,6}\s/g, '').replace(/\*\*/g, '').replace(/^-\s/gm, '• ')
    : rawPrompt;
  const promptPreview = promptFull.length > PROMPT_PREVIEW_LEN
    ? promptFull.slice(0, PROMPT_PREVIEW_LEN).replace(/\s\S*$/, '') + '…'
    : promptFull;

  return (
    <div
      className={`entry ${['image','video'].includes(entry.type) ? 'entry-media' : ''} ${selected && !selectMode ? 'selected' : ''} ${isChecked ? 'selected' : ''}`}
      onClick={handleClick}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
      onMouseEnter={e => { if (e.pointerType !== 'touch') setHovered(true); }}
      onMouseLeave={() => setHovered(false)}
      draggable={!!onDragStart}
      onDragStart={e => {
        if (onDragStart) {
          isDragging.current = true;
          clearTimeout(pressTimer.current); // cancel long press on drag start
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', entry.id);
          onDragStart(entry.id);
        }
      }}
      onDragEnd={() => { isDragging.current = false; }}
      style={{ userSelect: 'none', position: 'relative', cursor: onDragStart ? 'grab' : 'pointer', WebkitTapHighlightColor: 'transparent' }}
    >
      {/* Desktop hover checkbox — shows on hover or in select mode */}
      {onCheck && (hovered || selectMode || isChecked) && (
        <div
          onClick={e => { e.stopPropagation(); onCheck(entry.id); }}
          style={{
            position: 'absolute', top: 10, right: 10, zIndex: 3,
            width: 18, height: 18, borderRadius: 4, flexShrink: 0,
            border: `1.5px solid ${isChecked ? 'var(--accent)' : 'var(--border2)'}`,
            background: isChecked ? 'var(--accent)' : 'rgba(var(--surface-rgb, 30,30,30),0.85)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', transition: 'all 0.12s',
            // Only show on desktop hover (not mobile — mobile uses long press)
            opacity: isChecked || selectMode ? 1 : (hovered ? 0.9 : 0),
          }}>
          {isChecked && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
        </div>
      )}


      {/* Thumbnail */}
      <div className="entry-thumb">
        {entry.file_url && entry.type === 'image' ? (
          <img src={entry.file_url} alt={entry.title} />
        ) : entry.file_url && entry.type === 'video' ? (
          // Show video poster thumbnail with play icon overlay
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <video
              src={entry.file_url}
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 7 }}
              muted preload="metadata"
              onLoadedMetadata={e => { e.target.currentTime = 1; }}
            />
            <div style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.25)', borderRadius: 7,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'rgba(255,255,255,0.9)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="10" height="12" viewBox="0 0 10 12" fill="#000">
                  <path d="M0 0L10 6L0 12Z"/>
                </svg>
              </div>
            </div>
          </div>
        ) : (
          TYPE_ICONS[entry.type] || '📄'
        )}
      </div>

      <div className="entry-body">
        <div className="entry-top">
          <div className="entry-title">{entry.title}</div>
          <div className="entry-date">{formatDate(entry.created_at || entry.date)}</div>
        </div>

        {/* Truncated prompt preview */}
        <div className="entry-prompt" title={promptFull}>
          {promptPreview}
        </div>

        <div className="entry-meta">
          <span className={`tag ${TYPE_TAG_CLASS[entry.type]}`}>{TYPE_LABELS[entry.type]}</span>
          {entry.model && (() => {
            const platform = detectPlatform(entry.model);
            return (
              <span className="model-badge" style={platform ? {
                background: `${platform.color}15`,
                border: `0.5px solid ${platform.color}40`,
                color: platform.color,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              } : {}}>
                {platform && (
                  <img
                    src={platform.logo}
                    alt={platform.name}
                    style={{ width: 11, height: 11, objectFit: 'contain', flexShrink: 0 }}
                    onError={e => e.target.style.display = 'none'}
                  />
                )}
                {entry.model}
              </span>
            );
          })()}
          {entry.folders?.name && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 7px', borderRadius: 4, fontSize: 11,
              background: `${entry.folders.color}22`,
              border: `0.5px solid ${entry.folders.color}55`,
              color: entry.folders.color,
            }}>
              ▶ {entry.folders.name}
            </span>
          )}
          {(entry.tags || []).slice(0, 2).map(tag => (
            <span key={tag} className="tag tag-custom">{tag}</span>
          ))}
          {!entry.is_public && (
            <span className="tag" style={{ background: 'rgba(95,94,90,0.12)', color: 'var(--dim)', fontSize: 10 }}>
              private
            </span>
          )}
          {/* Copy count — public entries */}
          {entry.is_public && (entry.copy_count || 0) > 0 && (
            <span className="entry-meta-count" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--dim)' }}
              title={`Copied ${entry.copy_count} times`}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect x="9" y="9" width="13" height="13" rx="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              {entry.copy_count}
            </span>
          )}
          {/* Bookmark count — public entries */}
          {entry.is_public && (entry.bookmark_count || 0) > 0 && (
            <span className="entry-meta-count" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#f59e0b' }}
              title={`Bookmarked ${entry.bookmark_count} times`}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              {entry.bookmark_count}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
